import {
  CrossIcon,
  DownArrowIcon,
  DownloadIcon,
  RightTailArrowIcon,
  searchFilter as SearchFilterIcon,
  UpArrowIcon,
} from '@/assets/icons/icons';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Divider, Flex, Menu, message, Spin } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CustomPagination, CustomTitle, SearchInput } from '../../components';

import {
  fetchShipmentProducts,
  fetchShipmentProductsDownload,
} from '@/apis/shipment';
import { ProductDetailColumns, ShipmentProductList } from '@/apis/types';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import ShipmentDetailProductsTable from './components/ShipmentDetailProductsTable';

interface ShipmentProductsListDrawerProps {
  toggleDrawer: (
    value: boolean | null,
    name?: string | null,
    shipmentId?: string | null
  ) => void;
  shipmentId: string;
  legType?: 'CL' | 'EL';
}

const ShipmentProductsListDrawer: React.FC<ShipmentProductsListDrawerProps> = ({
  toggleDrawer,
  shipmentId,
  legType,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [productList, setProductList] = useState<ShipmentProductList[]>([]);
  const [productCount, setProductCount] = useState<number>(0);
  const [detailColumns, setDetailColumns] = useState<
    ProductDetailColumns | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [pageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // const [maxPageSize, setMaxPageSize] = useState(0);
  const [sortOrder, setSortOrder] = useState<'ascending' | 'descending'>(
    'descending'
  );
  const [sortKey, setSortKey] = useState<'name' | 'quantity' | 'temperature'>(
    'name'
  );

  const handlePageChange = (newPage: number) => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
    }, 1000);
  };

  const handleCloseSearch = () => {
    setIsSearchVisible(false);
    setSearchQuery('');
  };

  const handleSortOrderToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSortOrder((prev) => (prev === 'ascending' ? 'descending' : 'ascending'));
  };

  const parsed = shipmentId.trim().startsWith('{')
    ? JSON.parse(shipmentId)
    : null;
  const is_multiple_shipments =
    parsed && typeof parsed === 'object' && 'select_all' in parsed;
  const fetchProductLists = useCallback(() => {
    setIsLoading(true);
    // showSpin();

    let shipmentPayload = {};
    try {
      const parsed = shipmentId.trim().startsWith('{')
        ? JSON.parse(shipmentId)
        : null;
      if (parsed && typeof parsed === 'object' && 'select_all' in parsed) {
        // Multi-shipment
        shipmentPayload =
          parsed.select_all === true
            ? {
                select_all: true,
                exclude_shipment_ids: parsed.exclude_shipment_ids || [],
                filters: parsed.filters || {},
                ...(parsed.start ? { start: parsed.start } : {}),
                ...(parsed.end ? { end: parsed.end } : {}),
                ...(parsed.view_by ? { view_by: parsed.view_by } : {}),
              }
            : {
                select_all: false,
                ...(legType === 'EL'
                  ? { execution_leg_ids: parsed.shipment_ids || [] }
                  : { shipment_ids: parsed.shipment_ids || [] }),
              };
      } else {
        // Single shipment
        shipmentPayload =
          legType === 'EL'
            ? { execution_leg_id: shipmentId }
            : { shipment_id: shipmentId };
      }
    } catch {
      // fallback single shipment on parse error
      shipmentPayload =
        legType === 'EL'
          ? { execution_leg_id: shipmentId }
          : { shipment_id: shipmentId };
    }

    const model = {
      ...shipmentPayload,
      search: searchQuery,
      sort_key: sortKey,
      sort_order: sortOrder,
      page,
      page_size: pageSize,
    };

    fetchShipmentProducts(token ?? '', model, legType)
      .then((res) => {
        const data = res?.data ? res?.data : null;
        if (data) {
          if (data?.data?.is_success) {
            const res = data?.data?.data;
            if (res) {
              setProductList(res?.products as ShipmentProductList[]);
              setProductCount(res?.products_count);
              setDetailColumns(res?.product_detail_columns);
              setTotalPages(res?.page_count);
              // setMaxPageSize(res?.total_count);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        // hideSpin();
        setIsLoading(false);
      });
  }, [
    token,
    unauthorize,
    shipmentId,
    searchQuery,
    sortKey,
    sortOrder,
    page,
    pageSize,
    legType,
  ]);

  const downloadProductCSV = () => {
    showSpin();

    let shipmentPayload = {};
    try {
      const parsed = shipmentId.trim().startsWith('{')
        ? JSON.parse(shipmentId)
        : null;
      if (parsed && typeof parsed === 'object' && 'select_all' in parsed) {
        // Multi-shipment
        shipmentPayload =
          parsed.select_all === true
            ? {
                select_all: true,
                exclude_shipment_ids: parsed.exclude_shipment_ids || [],
                filters: parsed.filters || {},
                ...(parsed.start ? { start: parsed.start } : {}),
                ...(parsed.end ? { end: parsed.end } : {}),
                ...(parsed.view_by ? { view_by: parsed.view_by } : {}),
              }
            : {
                select_all: false,
                ...(legType === 'EL'
                  ? { execution_leg_ids: parsed.shipment_ids || [] }
                  : { shipment_ids: parsed.shipment_ids || [] }),
              };
      } else {
        // Single shipment
        shipmentPayload =
          legType === 'EL'
            ? { execution_leg_id: shipmentId }
            : { shipment_id: shipmentId };
      }
    } catch {
      // fallback single shipment on parse error
      shipmentPayload =
        legType === 'EL'
          ? { execution_leg_id: shipmentId }
          : { shipment_id: shipmentId };
    }

    const model = {
      ...shipmentPayload,
      file_type: 'csv',
      search: searchQuery,
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    fetchShipmentProductsDownload(token ?? '', model, legType)
      .then((res) => {
        const blob = new Blob([res.data], { type: 'text/csv' });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'shipment-products.csv';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    fetchProductLists();
  }, [fetchProductLists]);

  // Click outside to close functionality
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const isDropdownClick =
        target.closest('.ant-dropdown') ||
        target.closest('.ant-dropdown-menu') ||
        target.closest('.ant-select') ||
        target.closest('.ant-select-dropdown');
      if (isDropdownClick) {
        return;
      }
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        toggleDrawer(null, null, null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [toggleDrawer]);

  const productSortOptions = [
    { key: 'name', label: 'Name' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'temperature', label: 'Temperature Range' },
  ];

  const onSortChange = ({ key }: { key: string }) => {
    setSortKey(key as 'name' | 'quantity' | 'temperature');
  };

  const sortMenu = (
    <Menu selectedKeys={[sortKey]} onClick={onSortChange}>
      {productSortOptions.map((opt) => (
        <Menu.Item style={{ color: 'var(--text-primary)' }} key={opt?.key}>
          {opt?.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div className='h-full' ref={drawerRef}>
      <Flex gap={16} justify='space-between' align='center'>
        <span
          className='c-pointer products-drawer-back'
          onClick={() => {
            if (is_multiple_shipments) {
              toggleDrawer(null, null, null);
            } else {
              toggleDrawer(true, 'Detail');
            }
          }}
        >
          <RightTailArrowIcon />
        </span>
        <span
          title='Close'
          className='no-bg c-pointer'
          onClick={() => toggleDrawer(null, 'all', null)}
        >
          <CloseOutlined
            color='var(--text-primary)'
            style={{ fontSize: '14px', color: 'var(--text-primary)' }}
          />
        </span>
      </Flex>

      <div className='contact-form-header-delete products-list'>
        <span
          className={`arrow-icon h-4 `}
          onClick={() => downloadProductCSV()}
        >
          <DownloadIcon />
        </span>
      </div>

      <CustomTitle
        text={`Products (${productCount})`}
        fontSize={18}
        fontWeight={700}
        lineHeight='24px'
        color='var(--text-primary)'
        marginTop='24px'
        marginBottom='0'
      />

      <Divider style={{ margin: '8px 0' }} />

      <Flex
        wrap
        justify='space-between'
        align='center'
        gap={12}
        style={{ margin: '8px 0' }}
      >
        {isSearchVisible ? (
          <Flex gap={8}>
            <SearchInput
              borderWidth='2px'
              placeholder=''
              defaultValue={searchQuery}
              onChange={(val: string) => handleSearchChange(val)}
              onClear={() => handleSearchChange('')}
              style={{
                flex: 1,
                paddingRight: 10,
              }}
              prefixIcon={<SearchFilterIcon width={12} height={12} />}
            />
            <button
              onClick={handleCloseSearch}
              className='zl-btn icon-btn-28 radius-full'
            >
              <CrossIcon />
            </button>
          </Flex>
        ) : (
          <Flex
            align='center'
            justify='center'
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: 'var(--light-gray)',
              cursor: 'pointer',
            }}
            onClick={() => setIsSearchVisible(true)}
          >
            <SearchOutlined style={{ fontSize: '12px' }} />
          </Flex>
        )}
        <Dropdown overlay={sortMenu} trigger={['click']}>
          <Flex gap={6} align='center' className='cursor-pointer'>
            <CustomTitle
              text='Sort by:'
              fontWeight='400'
              fontSize='12px'
              lineHeight='20px'
              marginBottom={0}
              marginTop={0}
            />
            <CustomTitle
              text={
                productSortOptions.find((o) => o.key === sortKey)?.label || ''
              }
              fontWeight='600'
              fontSize='12px'
              marginBottom={0}
              marginTop={0}
            />

            <div className='sort-arrow-wrapper' onClick={handleSortOrderToggle}>
              {sortOrder === 'ascending' ? (
                <UpArrowIcon size={14} color='var(--text-primary)' />
              ) : (
                <DownArrowIcon size={14} color='var(--text-primary)' />
              )}
            </div>
          </Flex>
        </Dropdown>
      </Flex>

      <Divider style={{ margin: '8px 0' }} />

      {/* Table */}
      {isLoading ? (
        <Flex
          vertical
          gap={8}
          justify='center'
          align='center'
          style={{ height: '200px' }}
        >
          <Spin size='default'></Spin>
          <span className='disabled-row font-12'>Loading...</span>
        </Flex>
      ) : (
        <ShipmentDetailProductsTable
          products={productList}
          previewCount={null}
          detailColumns={detailColumns}
        />
      )}

      {totalPages > 1 && (
        <Flex style={{ width: '100%', marginTop: '12px' }} justify='center'>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Flex>
      )}
    </div>
  );
};

export default ShipmentProductsListDrawer;
